import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { ChangeEvent, useRef, useState } from "react";
import {
  editAppealCouncilLitigation,
  editAppealLitigation,
  editComplaint,
} from "services/prosecutorDecipilineService";
import { Complaints, Litigation } from "types";
import { LoadingIcon } from "utils/constant";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { ROLES } from "utils/roles";
import "../../complaintRequest/styles/AddEvidenceModal.css";
import { useTranslation } from "contexts/i18nContext";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  litigationData: Litigation;
  complaintData: Complaints;
  onRefresh: any;
}

export default function TerminateAppealLitigationModal({
  isOpen,
  setIsOpen,
  litigationData,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const canTerminateInspectionDailyHearing = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canTerminateCouncilDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState({
    files: [] as File[],
    termination_reason: "",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!litigationData) return;

    setLoading(true);

    const data = new FormData();
    data.append("termination_document", formState?.files[0] || "");
    data.append("termination_reason", formState.termination_reason);
    data.append(
      "is_final_verdict_given",
      String(litigationData?.is_final_verdict_given),
    );
    litigationData?.summon &&
      data.append("summon", String(litigationData?.summon));

    litigationData?.summonappeal_accusepProsecutor &&
      data.append(
        "summonappeal_accusepProsecutor",
        String(litigationData?.summonappeal_accusepProsecutor),
      );
    litigationData?.appealto_court &&
      data.append("appealto_court", String(litigationData?.appealto_court));

    const litigationId = litigationData?.id || "";
    const isFormData = true;

    try {
      const api = canTerminateInspectionDailyHearing
        ? editAppealLitigation
        : canTerminateCouncilDailyHearing
          ? editAppealCouncilLitigation
          : null;

      if (!api) {
        showToast(t("noPermissionToCreateDailyHearing"), "error");
        return;
      }

      const resData = await api(data, litigationId, isFormData);

      if (resData) {
        await editComplaint({
          ...complaintData,
          status: COMPLIANT_STATUS.CLOSED,
        });
      }

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        files: [] as File[],
        termination_reason: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("terminateLitigation")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("terminationReason")}
            </label>
            <textarea
              required
              rows={3}
              name="termination_reason"
              value={formState.termination_reason}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("terminationFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState.files.length > 0 && (
              <div className="file-list">
                {formState.files.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <button
          type="submit"
          className="bg-red-700 w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}...
            </div>
          ) : (
            t("terminate")
          )}
        </button>
      </form>
    </SideModal>
  );
}
